'use client';

import Loading from '@/app/(withLayout)/loading';
import { setAdditionalData } from '@/redux/additionalDataSlice';
import { appendVariables, pushAccessKey, pushDummyStep, pushFlowData, pushInitializeStepData } from '@/redux/flowSlice';
import { pushErrorReason, pushStepName } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import {
  additionalDataType,
  appendVariablesMap,
  initializeStep,
  initializeVariablesMap,
  instanceResponse,
  step,
  stepNames,
  steps,
  variables,
} from '@repo/onb-api';
import { deleteClientCookie, setClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendOnbEvent } from './newRelic';

/**
 * @todo this should receive initstep also from server if posible
 *
 * @param {instanceResponse} initialData - The initial flow data.
 * @return {React.JSX.Element} The JSX element representing the loading state.
 */
// export function ResumeClientFlow({
//   initialData,
//   children,
// }: {
//   initialData: instanceResponse;
//   children: React.JSX.Element;
// }): React.JSX.Element {
//   const dispatch = useDispatch();
//   const router = useRouter();
//   const lastStep = initialData.steps?.[initialData?.steps?.length - 1]?.name;
//   // router.replace(lastStep ? `/flow/${lastStep}` : '/error');
//   if (typeof window !== 'undefined') window.history.replaceState(null, '', lastStep ? `/flow/${lastStep}` : '/error');
//   const storeLastStep = useSelector(
//     (state: RootState) => state.flowdata.steps?.[state.flowdata.steps?.length - 1]?.name,
//   );
//   useEffect(() => {
//     dispatch(pushFlowData(initialData));
//   }, [dispatch, router, initialData]);
//   return storeLastStep === lastStep ? children : <Loading />;
// }

/**
 * Initializes the client flow by loading the initial flow state with Redux and navigating to the last step of the flow.
 *
 * @param {instanceResponse} initialData - The initial flow data.
 * @param {string} accessKey - The accessKey string to set in redux.
 * @param {string} utmData - The searchParams utmData string to set as cookie.
 * @return {React.JSX.Element} The JSX element representing the loading state.
 */
export function InitializeClientFlow({
  initialData,
  accessKey,
  utmData,
}: {
  initialData: instanceResponse;
  accessKey?: string;
  utmData?: string;
}): React.JSX.Element {
  const dispatch = useDispatch();
  const router = useRouter();
  useEffect(() => {
    if (accessKey && initialData?.id) {
      // if another instace id was present, replace with the new one
      deleteClientCookie(['orderToken', 'utmData', 'accessKey']);
      setClientCookie('instanceId', initialData.id);
    }
    if (accessKey) {
      dispatch(pushAccessKey(accessKey));
      setClientCookie('accessKey', accessKey);
    }
    dispatch(pushFlowData(initialData));
    const additionalData = JSON.parse(initialData.variables?.additionalData || '{}') as additionalDataType;
    const utmValue = utmData && JSON.parse(utmData)?.length ? utmData : additionalData.utmData;
    dispatch(setAdditionalData({ ...JSON.parse(initialData.variables?.additionalData || '{}'), utmData: utmValue }));
    const lastStep = initialData.steps?.[initialData?.steps?.length - 1]?.name;
    router.replace(lastStep ? `/flow/${lastStep}` : '/error');
  }, [dispatch, router, initialData]);
  return <Loading />;
}

// /**
//  * Initializes the client flow by loading the initial flow state with Redux and navigating to the last step of the flow.
//  *
//  * @param {instanceResponse} initialData - The initial flow data.
//  * @return {React.JSX.Element} The JSX element representing the loading state.
//  */
// export function InitializeServerCookies({ jwt, instanceId }: { jwt?: string; instanceId?: string }) {
//   if (jwt) setClientCookie('ecommerce_token', jwt);
//   if (instanceId) setClientCookie('instanceId', instanceId);
//   const router = useRouter();
//   router.replace('/flow');
//   return <Loading />;
// }

/**
 * Initializes the client flow by setting the dummy step in Redux and deleting the instanceId cookie.
 *
 * @param {stepNames} stepName - The name of the step to set as dummy.
 * @param {React.JSX.Element} children - The JSX element to render.
 * @param {string} utmData - The searchParams utmData string to set as cookie.
 * @return {React.JSX.Element} The JSX element representing the loading state.
 */
export function InitializeDummyFlow({
  stepName,
  children,
  utmData,
}: {
  stepName: stepNames;
  children: React.JSX.Element;
  utmData?: string;
}): React.JSX.Element {
  const dispatch = useDispatch();
  deleteClientCookie(['instanceId', 'orderToken', 'requestedAmount', 'utmData', 'accessKey']);
  useEffect(() => {
    dispatch(setAdditionalData({ requestedAmount: '' }));
    // deleteClientCookie(['requestedAmount']);
    const utmValue = utmData && JSON.parse(utmData)?.length ? utmData : '';
    dispatch(setAdditionalData({ utmData: utmValue }));
    dispatch(pushStepName(stepName));
    dispatch(pushDummyStep(stepName));
  }, [dispatch]);
  return children;
}

/**
 * Checks if the client data is correct and renders the provided children if it is.
 * If the client data is not correct, it redirects to the flow page.
 *
 * @param {stepNames} stepName - The name of the step to check.
 * @param {boolean} [skip] - Flag to skip the check. Defaults to false.
 * @param {React.JSX.Element} children - The JSX element to render if the client data is correct.
 * @return {React.JSX.Element} The JSX element to render.
 */
export function CheckClientData({
  instanceId,
  stepName,
  children,
}: {
  jwt?: string;
  instanceId?: string;
  stepName: stepNames;
  children: React.JSX.Element;
}): React.JSX.Element {
  // const date = new Date();
  // date.setTime(date.getTime() + 3 * 60 * 60 * 1000); // Adding 3 hours in milliseconds
  // const cookieTtl = date.toUTCString();

  // This cookie is set only in a 'onb-cuotas' instance type.
  if (instanceId) {
    // setClientCookie('instanceId', instanceId, cookieTtl);
    setClientCookie('instanceId', instanceId);
  }

  const router = useRouter();
  const dispatch = useDispatch();
  const { variables, steps, id, accessKey } = useSelector((state: RootState) => state.flowdata);
  const additionalData = useSelector((state: RootState) => state.additionalData);
  const [currentStep, setCurrentStep] = useState<step>();
  const [childWithProps, setChildWithProps] = useState<React.JSX.Element>();
  const [isInitialized, setIsInitialized] = useState(false);
  const [areVariablesAppended, setAreVariablesAppended] = useState(false);
  useEffect(() => {
    const _currentStep = steps.find((s) => s.name === stepName);
    if (!_currentStep) {
      // At this point we are missing the step data, redirect to the flow page
      router.replace('/flow');
      router.refresh();
    } else {
      setCurrentStep(_currentStep);
    }
  }, [steps, stepName]);
  useEffect(() => {
    if (currentStep?.initialized) {
      if (currentStep.isEndOfFlow) {
        deleteClientCookie([
          'instanceId',
          'orderToken',
          'integrationType',
          'policyProduct',
          'requestedAmount',
          'utmData',
          'accessKey',
        ]);
      }
      setChildWithProps(React.cloneElement(children, { stepdata: currentStep?.variables || {} }));
    }
  }, [currentStep, children]);
  useEffect(() => {
    if (!isInitialized && id && stepName === currentStep?.name && areVariablesAppended) {
      let error = false;
      setIsInitialized(true);
      initializeStep<steps[typeof stepName]>({
        instanceId: id,
        name: stepName,
        variables: getInitializeVariables({ name: stepName, variables }),
        accessKey: accessKey,
      })
        .then((response) => {
          if (response.hardError || response.softError) {
            sendOnbEvent({ type: 'error', subtype: 'initStep', data: { stepName } });
            error = true;
            if (response.error === 'REQUIRED_ATTRIBUTE' && response.attribute === 'accessKey') {
              dispatch(pushErrorReason('accessKey'));
            }
          } else {
            dispatch(pushInitializeStepData({ stepName, variables: response.variables }));
            dispatch(pushStepName(stepName));
          }
        })
        .catch(() => {
          console.error('Initialize Step Error');
          error = true;
        })
        .finally(() => {
          if (error) {
            sendOnbEvent({ type: 'error', subtype: 'initStep', data: { stepName } });
            router.push('/error');
          }
        });
    }
    //Will try to append local variables before step initialization
    if (!isInitialized && id && stepName === currentStep?.name && !areVariablesAppended) {
      const variablesToAppend = appendVariablesMap(stepName, additionalData);
      if (variablesToAppend) {
        dispatch(appendVariables({ variables: variablesToAppend }));
      }
      setAreVariablesAppended(true);
    }
  }, [isInitialized, id, stepName, dispatch, variables, currentStep, areVariablesAppended]);

  return childWithProps ? childWithProps : <Loading />;
}

/**
 * Returns an object with mapped initialize variables based on the provided step name and variables.
 *
 * @param {stepNames} name - The name of the step.
 * @param {variables} variables - The variables to be consumed.
 * @return {variables} An object with initialize variables.
 */
const getInitializeVariables = ({ name, variables }: { name: stepNames; variables?: variables }) => {
  return initializeVariablesMap[name]?.reduce((acc, { local: localVars, remote }) => {
    const local = localVars.find((v) => variables?.[v] !== undefined);
    return { ...acc, [remote]: local ? variables?.[local] : undefined };
  }, {} as variables);
};
